import { toast } from "sonner";
import { FollowupStepContentEditor } from "./followup-step-content-editor";
import { useEffect, useState } from "react";
import type { SettingsSectionState } from "./settings-section";
import { useQuery } from "@tanstack/react-query";
import type {
  FollowupSettingsDTO,
  SaveFollowups,
  FollowupStep,
  FollowupPreviewInput,
  FollowupPreviewDTO,
  WabaTemplateListDTO,
  ChannelDTO,
} from "@ai-crm/shared";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select } from "./ui/select";
import { Switch } from "./ui/switch";
type DraftStep = {
  value: string;
  unit: "hours" | "days";
  mode: FollowupStep["mode"];
  text: string;
  instruction: string;
  templateRef: string;
  values: string[];
};
export interface FollowupEditorServices {
  channels?:
    | Pick<
        ChannelDTO,
        "id" | "wazzupChannelId" | "physicalChannelId" | "transport"
      >[]
    | undefined;
  loadTemplates?:
    ((channelId: string) => Promise<WabaTemplateListDTO>) | undefined;
  preview?:
    | ((
        input: FollowupPreviewInput,
        signal: AbortSignal,
      ) => Promise<FollowupPreviewDTO>)
    | undefined;
}
const emptyContent = {
  mode: "FIXED" as const,
  text: "",
  instruction: "",
  templateRef: "",
  values: [] as string[],
};
interface SectionOptions {
  embedded?: boolean | undefined;
  disabled?: boolean | undefined;
  capabilitiesVersion?: number | undefined;
  onStateChange?: ((state: SettingsSectionState) => void) | undefined;
}
function draftSteps(settings: FollowupSettingsDTO): DraftStep[] {
  return settings.steps.length
    ? settings.steps.map((s) => ({
        value: String(
          s.afterSeconds / (s.afterSeconds % 86400 === 0 ? 86400 : 3600),
        ),
        unit: s.afterSeconds % 86400 === 0 ? "days" : "hours",
        ...emptyContent,
        ...s,
      }))
    : [1, 3, 7].map((n) => ({
        value: String(n),
        unit: "days",
        ...emptyContent,
      }));
}
export function FollowupSettings({
  queryKey,
  load,
  save,
  onSaved,
  hasChannels = true,
  reloadDependencies,
  channels,
  loadTemplates,
  preview,
  embedded,
  disabled,
  capabilitiesVersion,
  onStateChange,
}: {
  queryKey: string;
  load: () => Promise<FollowupSettingsDTO>;
  save: (input: SaveFollowups) => Promise<FollowupSettingsDTO>;
  onSaved?: () => void;
  hasChannels?: boolean;
  reloadDependencies?: () => Promise<void>;
} & FollowupEditorServices &
  SectionOptions) {
  const query = useQuery({ queryKey: ["followups", queryKey], queryFn: load });
  useEffect(() => {
    if (!query.data)
      onStateChange?.({
        dirty: false,
        summary: query.isError
          ? "Не удалось загрузить касания"
          : "Загрузка касаний…",
      });
  }, [query.isError, query.data, onStateChange]);
  if (query.isPending)
    return (
      <div role="status" className="h-24 animate-pulse rounded bg-muted">
        Загрузка касаний…
      </div>
    );
  if (!query.data)
    return (
      <div role="alert">
        Не удалось загрузить касания.{" "}
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => void query.refetch()}
        >
          Повторить
        </Button>
      </div>
    );
  return (
    <FollowupForm
      key={queryKey}
      initial={query.data}
      embedded={embedded}
      disabled={disabled}
      capabilitiesVersion={capabilitiesVersion}
      onStateChange={onStateChange}
      channels={channels}
      loadTemplates={loadTemplates}
      preview={preview}
      reload={async () => {
        await reloadDependencies?.();
        return (await query.refetch({ throwOnError: true })).data ?? query.data;
      }}
      save={save}
      onSaved={onSaved}
      hasChannels={hasChannels}
    />
  );
}
function FollowupForm({
  initial,
  reload,
  save,
  onSaved,
  hasChannels,
  channels,
  loadTemplates,
  preview,
  embedded,
  disabled,
  capabilitiesVersion,
  onStateChange,
}: {
  initial: FollowupSettingsDTO;
  reload: () => Promise<FollowupSettingsDTO>;
  save: (input: SaveFollowups) => Promise<FollowupSettingsDTO>;
  onSaved?: (() => void) | undefined;
  hasChannels: boolean;
} & FollowupEditorServices &
  SectionOptions) {
  const [base, setBase] = useState(initial);
  const [enabled, setEnabled] = useState(initial.enabled);
  const [steps, setSteps] = useState<DraftStep[]>(() => draftSteps(initial));
  const [baseline, setBaseline] = useState(() =>
    JSON.stringify([initial.enabled, draftSteps(initial), ""]),
  );
  const [apply, setApply] = useState<"ALL" | "NEW_ONLY" | "">("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = baseline !== JSON.stringify([enabled, steps, apply]);
  const summary = base.enabled
    ? `Включены · шагов: ${base.steps.length}`
    : "Выключены";
  useEffect(() => {
    onStateChange?.({ dirty, summary, error: !!error });
  }, [dirty, summary, error, onStateChange]);
  function resetDraft(data: FollowupSettingsDTO) {
    setEnabled(data.enabled);
    setSteps(draftSteps(data));
    setApply("");
    setBaseline(JSON.stringify([data.enabled, draftSteps(data), ""]));
  }
  const payload: FollowupStep[] = steps.map((s, i) => ({
    ordinal: i + 1,
    afterSeconds: Number(s.value) * (s.unit === "days" ? 86400 : 3600),
    ...(s.mode === "FIXED"
      ? { mode: "FIXED" as const, text: s.text.trim() }
      : s.mode === "PROMPT"
        ? { mode: "PROMPT" as const, instruction: s.instruction.trim() }
        : {
            mode: "WABA_TEMPLATE" as const,
            templateRef: s.templateRef,
            values: s.values,
          }),
  }));
  const valid =
    (!enabled || payload.length > 0) &&
    payload.every(
      (s, i) =>
        Number.isSafeInteger(s.afterSeconds) &&
        s.afterSeconds > 0 &&
        s.afterSeconds * 1000 <= Number.MAX_SAFE_INTEGER &&
        (s.mode === "FIXED"
          ? s.text.length > 0 && s.text.length <= 5000
          : s.mode === "PROMPT"
            ? s.instruction.length > 0 && s.instruction.length <= 10000
            : Boolean(s.templateRef) &&
              s.values.every((v) => v.trim().length > 0 && v.length <= 1000)) &&
        (!i || s.afterSeconds > payload[i - 1]!.afterSeconds),
    );
  function update(i: number, patch: Partial<DraftStep>) {
    setSteps((s) => s.map((v, n) => (i === n ? { ...v, ...patch } : v)));
  }
  async function reloadVersions() {
    setPending(true);
    setError(null);
    try {
      setBase(await reload());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось обновить версии");
    } finally {
      setPending(false);
    }
  }
  async function submit() {
    if (pending || disabled || !valid || !apply || !dirty) return;
    setPending(true);
    setError(null);
    try {
      const result = await save({
        enabled,
        expectedVersion: base.version,
        expectedCapabilitiesVersion:
          capabilitiesVersion ?? base.capabilitiesVersion,
        apply,
        steps: payload,
      });
      setBase(result);
      resetDraft(result);
      toast.success("Настройки касаний сохранены");
      onSaved?.();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Не удалось сохранить касания. Черновик сохранён в форме.",
      );
    } finally {
      setPending(false);
    }
  }
  return (
    <fieldset
      className={
        embedded
          ? "min-w-0 space-y-4"
          : "space-y-4 rounded-lg border bg-card p-4"
      }
      disabled={pending || disabled}
    >
      {!embedded && (
        <legend className="px-1 text-sm font-semibold">
          Повторные касания
        </legend>
      )}
      <p className="text-sm text-muted-foreground">
        Интервалы отсчитываются от подтверждённого обычного исходящего
        сообщения. Рабочие этапы общие с агентом. Сообщение менеджера не
        сбрасывает серию; ответ клиента завершает её.
      </p>
      {!hasChannels && (
        <p role="status">
          Для отправки подключите канал в{" "}
          <a href="/settings/wazzup">настройках Wazzup</a>.
        </p>
      )}
      <p className="text-sm text-muted-foreground">
        При недостаточной истории или неизвестном времени сообщения касания
        будут ждать подтверждения.
      </p>
      <label className="flex items-center justify-between gap-4 rounded-md border p-3 text-sm font-medium">
        <span>Включить касания</span>
        <Switch
          aria-label="Включить касания"
          checked={enabled}
          onCheckedChange={(checked) => {
            setEnabled(checked);
          }}
        />{" "}
      </label>
      <p className="text-sm text-muted-foreground">
        Текст и генерация работают в Telegram и обычном WhatsApp. В WhatsApp
        Business после 24 часов без сообщения клиента нужен одобренный шаблон.
        Канал или режим автоматически не подменяются; шаблоны могут
        тарифицироваться провайдером.
      </p>
      {!steps.length && <p>Шагов пока нет. Добавьте интервал и текст.</p>}
      {steps.map((s, i) => (
        <div key={i} className="space-y-4 rounded-lg border bg-background p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="block flex-1 space-y-2 text-sm font-medium">
              Шаг {i + 1}: интервал
              <Input
                aria-label={`Интервал шага ${i + 1}`}
                className="sm:max-w-32"
                type="number"
                min="0"
                step="any"
                value={s.value}
                onChange={(e) => update(i, { value: e.target.value })}
              />
            </label>
            <Select
              className="sm:max-w-40"
              aria-label={`Единица шага ${i + 1}`}
              value={s.unit}
              onChange={(e) =>
                update(i, { unit: e.target.value as DraftStep["unit"] })
              }
            >
              <option value="hours">часов</option>
              <option value="days">дней</option>
            </Select>
          </div>
          <FollowupStepContentEditor
            index={i}
            draft={s}
            step={payload[i]!}
            update={(patch) => update(i, patch)}
            versions={{
              expectedVersion: base.version,
              expectedCapabilitiesVersion:
                capabilitiesVersion ?? base.capabilitiesVersion,
            }}
            channels={channels}
            loadTemplates={loadTemplates}
            preview={preview}
          />
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => {
              setSteps((v) => v.filter((_, n) => n !== i));
            }}
          >
            Удалить шаг {i + 1}
          </Button>
          {s.mode === "FIXED" && (
            <details>
              <summary>Предпросмотр текста</summary>
              <p className="whitespace-pre-wrap">
                {s.text || "Введите текст касания"}
              </p>
            </details>
          )}
        </div>
      ))}
      <Button
        variant="outline"
        type="button"
        onClick={() => {
          setSteps((v) => [...v, { value: "", unit: "days", ...emptyContent }]);
        }}
      >
        Добавить шаг
      </Button>
      {!valid && (
        <p role={enabled ? "alert" : "status"}>
          Заполните содержимое шагов и положительные возрастающие интервалы.
        </p>
      )}
      <label className="block space-y-2 text-sm font-medium">
        Применить изменения
        <Select
          aria-label="Применить изменения"
          value={apply}
          onChange={(e) => setApply(e.target.value as typeof apply)}
        >
          <option value="">Выберите область применения</option>
          <option value="ALL">К текущим и новым сериям</option>
          <option value="NEW_ONLY">Только к новым сериям</option>
        </Select>
      </label>
      {!apply && (
        <p role="status" className="text-sm text-muted-foreground">
          Выберите область применения.
        </p>
      )}
      <p className="text-sm text-muted-foreground">
        В обоих случаях дата отсчёта и номера выполненных шагов сохраняются.
        Только новые серии: текущие продолжат прежние шаги. Сохранение не
        отправляет сообщения сразу. Отключение сохраняет прогресс.
      </p>
      <Button
        type="button"
        disabled={!valid || !apply || pending || disabled || !dirty}
        onClick={() => void submit()}
      >
        {pending ? "Сохранение…" : "Сохранить касания"}
      </Button>
      <Button
        type="button"
        variant="outline"
        disabled={!dirty || pending || disabled}
        onClick={() => {
          resetDraft(base);
          setError(null);
        }}
      >
        Отменить
      </Button>
      {error && (
        <div>
          <p role="alert">{error} Черновик не потерян.</p>
          <Button
            variant="outline"
            size="sm"
            className="h-auto min-h-8 whitespace-normal py-1.5"
            type="button"
            onClick={() => {
              void reloadVersions();
            }}
          >
            Обновить версии, сохранив черновик
          </Button>
        </div>
      )}
    </fieldset>
  );
}
